import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import type { AnchorHint } from './ExpandedResultGroups';

interface UsePaletteSelectionArgs {
  /** Whether the expanded palette is active (flag on, navigate mode). */
  readonly expanded: boolean;
  /** Current query text (plus the scope) — a change is fresh intent and releases the anchor. */
  readonly query: string;
  /** Top / first-static ids reported up by ExpandedResultGroups. */
  readonly anchorHint: AnchorHint;
  /** First resolved content row id (highlight priority 2, after Top). */
  readonly firstContentId: string | undefined;
  /** Hand-off invoked by the Tab shortcut. */
  readonly onAskBird: () => void;
}

interface UsePaletteSelectionResult {
  readonly selectedValue: string;
  readonly setSelectedValue: (value: string) => void;
  readonly handleCommandKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  /** The pointer moved over the list: the hovered row is a choice, freeze it. */
  readonly handlePointerMove: (e: PointerEvent<HTMLDivElement>) => void;
}

/**
 * The Cmd-K "highlight contract" as a cohesive unit.
 *
 * `selectedValue` drives the controlled cmdk highlight. While the user hasn't
 * arrowed (`userMovedRef` false), the highlight auto-anchors to the best
 * available target — Top result → first content row → first static entry →
 * the pinned Ask Bird row — recomputed as async groups resolve. The first
 * ArrowUp/ArrowDown, or the pointer moving over the list (cmdk selects the
 * hovered row), freezes that auto-anchoring so late-arriving groups can never
 * steal the user's selection; a new query releases it again. Tab hands the
 * query off to Bird.
 */
export function usePaletteSelection({
  expanded,
  query,
  anchorHint,
  firstContentId,
  onAskBird,
}: UsePaletteSelectionArgs): UsePaletteSelectionResult {
  const [selectedValue, setSelectedValue] = useState('');
  const userMovedRef = useRef(false);

  // A new query is fresh intent — release the user's manual selection so
  // auto-anchoring resumes. Defined before the anchor effect so it runs first.
  useEffect(() => {
    userMovedRef.current = false;
  }, [query]);

  // Re-point the freeze when the row the user parked on stops being rendered.
  //
  // Freezing on ArrowUp/Down protects the selection from late-arriving GROUPS,
  // but a row can also vanish WITHIN a query: the Help group paints curated
  // `help-*` rows, then swaps them for live rows keyed by article URL. Because
  // StaticResultGroup renders `forceMount`, cmdk never registers these items
  // and its own "selected item unmounted" recovery never fires — so the
  // highlight would point at nothing and Enter would be a silent no-op.
  //
  // RE-POINT, don't release. Clearing `userMovedRef` looks like the fix and is
  // worse: the anchor effect below then re-anchors to `firstStaticId`, which is
  // the first row of the WHOLE static set (a "Go to" row), so a user who had
  // arrowed into Help gets Enter performing an unrelated navigation command —
  // a wrong action instead of a dead one. Holding the user's POSITION keeps
  // them in the group they were reading.
  //
  // Deliberately narrow: it only fires for an id that WAS in the static set and
  // no longer is. Content rows are not in `staticRows` and must never trip it.
  const prevStaticRowsRef = useRef<AnchorHint['staticRows']>([]);
  useEffect(() => {
    const prev = prevStaticRowsRef.current;
    prevStaticRowsRef.current = anchorHint.staticRows;
    if (!expanded || !userMovedRef.current || !selectedValue) return;

    const wasAt = prev.findIndex((row) => row.id === selectedValue);
    if (wasAt < 0) return;
    if (anchorHint.staticRows.some((row) => row.id === selectedValue)) return;

    // Re-point INSIDE the group the user was reading. Clamping across the whole
    // flattened set walks off the end of an emptying group into the next one —
    // e.g. Help empties and the highlight lands on a Settings row, so Enter
    // opens a settings tab. That is the wrong-Enter this block exists to stop.
    const group = prev[wasAt]?.group;
    const sameGroup = anchorHint.staticRows.filter((row) => row.group === group);
    if (sameGroup.length === 0) {
      // The group is gone entirely — release to auto-anchoring rather than
      // guessing at an unrelated row.
      userMovedRef.current = false;
      return;
    }
    const offsetInGroup = prev.slice(0, wasAt).filter((row) => row.group === group).length;
    const next = sameGroup[Math.min(offsetInGroup, sameGroup.length - 1)];
    if (next) setSelectedValue(next.id);
  }, [expanded, anchorHint, selectedValue]);

  // `query` is a dependency so a release re-anchors even when the anchor
  // targets did not change: after "Show all" the first row is often the same
  // id it was before, and without this the highlight stayed where the pointer
  // left it.
  useEffect(() => {
    if (!expanded || userMovedRef.current) return;
    // Ask Bird now lives in a pinned footer (not a list item), so there's no
    // 'ask-bird' value to fall back to — when nothing matches, select nothing.
    setSelectedValue(anchorHint.topId ?? firstContentId ?? anchorHint.firstStaticId ?? '');
  }, [expanded, anchorHint, firstContentId, query]);

  const handleCommandKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        userMovedRef.current = true;
        return;
      }
      // Tab hands off to Bird ONLY from the search input, and only unmodified.
      //
      // This handler sits on the cmdk root, so it sees Tab from every
      // descendant — including the Types menu's own search field, which is
      // inside the palette and auto-focused on open. Chakra's menu keymap
      // calls `preventDefault` on Tab but NOT `stopPropagation`, and cmdk
      // invokes `props.onKeyDown` before its own `defaultPrevented` guard, so
      // an unscoped handler tore the whole palette down mid-typing. Shift-Tab
      // is excluded for the same reason: it means "go back", never "ask Bird".
      const fromSearchInput = (e.target as HTMLElement | null)?.hasAttribute('cmdk-input');
      if (e.key === 'Tab' && fromSearchInput && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        onAskBird();
      }
    },
    [onAskBird],
  );

  // Real movement only. A list that shifts under a still cursor makes the
  // browser re-target hover without the hand moving; that is not a choice.
  // Over a result only: the root also holds the input and the filter row, and a
  // hand moving there has chosen nothing.
  const handlePointerMove = useCallback((e: PointerEvent<HTMLDivElement>) => {
    const overItem = (e.target as Element | null)?.closest?.('[cmdk-item]');
    if (overItem && (e.movementX !== 0 || e.movementY !== 0)) userMovedRef.current = true;
  }, []);

  return { selectedValue, setSelectedValue, handleCommandKeyDown, handlePointerMove };
}
